import {
  AHAnimatedPageIndicator,
  AHText,
  AHWidgetTitle,
  AHWidgetTitleLogo,
  AHWidgetTitleText,
  AHWidgetTitleWrapper,
  Box,
} from "arkh";
import { Tile } from "./Tile";
import { FlatList, Platform } from "react-native";
import Animated, {
  useDerivedValue,
  useSharedValue,
  useAnimatedScrollHandler,
} from "react-native-reanimated";
import { useWidgetLayout } from "../WidgetLayoutContext";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useInterval } from "../../useInterval";
import { graphql, useFragment } from "react-relay";
import { WidgetKPI_widgetKPI$key } from "./__generated__/WidgetKPI_widgetKPI.graphql";
import { BaseWidgetProps } from "../BaseProps";
import { Tile_kpiTile$key } from "./__generated__/Tile_kpiTile.graphql";
import { Image } from "expo-image";
import { FormattedMessage } from "react-intl";

type TileRow = [Tile_kpiTile$key | undefined, Tile_kpiTile$key | undefined];
type TilePage = TileRow[];

type Props = {
  widget: WidgetKPI_widgetKPI$key;
  storecastId?: string;
  mode?: "FOCUS" | "IDLE";
  interactive?: undefined | false;
} & BaseWidgetProps;

export const WidgetKPI = (props: Props) => {
  const widget = useFragment<WidgetKPI_widgetKPI$key>(
    graphql`
      fragment WidgetKPI_widgetKPI on WidgetKPI {
        id
        title
        dataSource
        tiles {
          ...Tile_kpiTile
        }
      }
    `,
    props.widget
  );

  const { height: widgetHeight, width: widgetWidth } = useWidgetLayout();
  const [layout, setLayout] = useState({
    width: widgetWidth,
    height: widgetHeight,
  });

  const rowsPerPage = useMemo(
    () => Math.round((layout.height / layout.width) * 2),
    [layout]
  );

  const pageStride = 2 * rowsPerPage;

  const pages = useMemo(
    () =>
      [
        ...new Array(((widget.tiles.length + pageStride - 1) / pageStride) | 0),
      ].map((_, pageI) =>
        [...new Array(rowsPerPage)].map(
          (_, rowI) =>
            [
              widget.tiles[pageI * pageStride + rowI * 2],
              widget.tiles[pageI * pageStride + rowI * 2 + 1],
            ] satisfies TileRow
        )
      ),
    [pageStride, rowsPerPage, widget.tiles]
  );

  const listRef = useRef<FlatList<TilePage>>(null);

  const scrollY = useSharedValue(0);

  const onScroll = useAnimatedScrollHandler({
    onScroll: (e) => {
      scrollY.value = e.contentOffset.y;
    },
  });

  const onNext = useCallback(() => {
    const currentPage = Math.floor(scrollY.value / layout.height);

    const nextPage = (currentPage + 1) % pages.length;
    listRef.current?.scrollToIndex({
      index: nextPage,
      animated: false,
    });
    scrollY.value = nextPage * layout.height;
    scrollY.modify(undefined, true);
  }, [listRef, pages.length, layout.height, scrollY]);

  useInterval(
    onNext,
    props.idiom === "STATIC" && pages.length > 1 ? 12000 : null
  );

  const pageIndicator = useDerivedValue(() => scrollY.value / layout.height);

  const title = useMemo(() => {
    switch (widget.dataSource) {
      case "FLOURISH_PULSE":
        return (
          <AHWidgetTitleLogo
            source={require("../WidgetIntegrationFlourishPraise/flourish_logo_darkpurple.webp")}
          />
        );
      case "INVAFRESH":
        return <AHWidgetTitleLogo source={require("./Upshop.png")} />;
      default:
        return widget.dataSource.startsWith("POWER_BI") ? (
          <AHWidgetTitleLogo
            source={require("./PowerBILogo.png")}
            subtitle={widget.title}
          />
        ) : (
          <AHWidgetTitle>{widget.title}</AHWidgetTitle>
        );
    }
  }, [widget.dataSource, widget.title]);

  const renderItem = useCallback(
    ({ item, index }: { item: TilePage; index: number }) => (
      <Box
        key={index}
        style={{
          height: layout.height,
        }}
      >
        {item.map((row, i) => (
          <Box key={i} style={{ flex: 1, flexDirection: "row" }}>
            <Box style={{ flex: 1 }} space="small">
              {row[0] && <Tile tileKey={row[0]} />}
            </Box>
            <Box style={{ flex: 1 }} space="small">
              {row[1] && <Tile tileKey={row[1]} />}
            </Box>
          </Box>
        ))}
      </Box>
    ),
    [layout.height]
  );

  return (
    <Box
      color="surface"
      cornerRadius="medium"
      style={{ width: "100%", height: "100%" }}
    >
      {title}
      <Box
        style={{ flex: 1 }}
        onLayout={(e) => setLayout(e.nativeEvent.layout)}
      >
        <Animated.FlatList
          ref={listRef}
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          showsVerticalScrollIndicator={false}
          scrollEnabled={pages.length > 1}
          data={pages}
          onScroll={onScroll}
          scrollEventThrottle={16}
          getItemLayout={(_, index) => ({
            length: layout.height,
            offset: layout.height * index,
            index,
          })}
          renderItem={renderItem}
          ListEmptyComponent={
            <AHText
              type="info-small"
              style={{ textAlign: "center" }}
              space="large"
            >
              <FormattedMessage
                description="WidgetKPI empty"
                defaultMessage="There is nothing to show here right now."
              />
            </AHText>
          }
        />
      </Box>
      {pages.length > 1 && (
        <Box
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            height: "100%",
            justifyContent: "center",
          }}
        >
          <AHAnimatedPageIndicator
            direction="vertical"
            page={pageIndicator}
            numberOfItems={pages.length}
          />
        </Box>
      )}
    </Box>
  );
};
